import { createTheme, Theme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    red: Palette['primary'];
    teal: Palette['primary'];
    green: Palette['primary'];
    black: Palette['primary'];
    violet: Palette['primary'];
  }
  interface PaletteOptions {
    red: PaletteOptions['primary'];
    teal: PaletteOptions['primary'];
    green: PaletteOptions['primary'];
    black: PaletteOptions['primary'];
    violet: PaletteOptions['primary'];
  }
}

export const theme: Theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#4ECDC4',
      light: '#7EDDD6',
      dark: '#3BA99F',
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: '#424242',
      light: '#6D6D6D',
      dark: '#1B1B1B',
      contrastText: '#FFFFFF'
    },
    background: {
      default: '#121212',
      paper: '#1E1E1E'
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#B0B0B0'
    },
    red: {
      main: '#EF5350'
    },
    teal: {
      main: '#4ECDC4'
    },
    violet: {
      main: '#AB47BC'
    },
    black: {
      main: '#000000'
    },
    green: {
      main: '#66BB6A'
    },
    divider: 'rgba(255, 255, 255, 0.12)',
    action: {
      active: '#4ECDC4',
      hover: 'rgba(78, 205, 196, 0.08)',
      selected: 'rgba(78, 205, 196, 0.16)',
      disabled: 'rgba(255, 255, 255, 0.3)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)'
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      letterSpacing: '-0.01562em'
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      letterSpacing: '-0.00833em'
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      letterSpacing: '0em'
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      letterSpacing: '0.00735em'
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      letterSpacing: '0em'
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      letterSpacing: '0.0075em'
    },
    button: {
      fontWeight: 600,
      textTransform: 'none'
    }
  },
  shape: {
    borderRadius: 12
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: 'outlined'
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.23)'
            },
            '&:hover fieldset': {
              borderColor: 'rgba(78, 205, 196, 0.5)'
            },
            '&.Mui-focused fieldset': {
              borderColor: '#4ECDC4'
            }
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '0.95rem',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(78, 205, 196, 0.3)'
          }
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 12px rgba(78, 205, 196, 0.4)'
          }
        },
        outlined: {
          borderColor: '#878787',
          color: '#FFFFFF',
          '&:hover': {
            borderColor: '#878787',
            backgroundColor: 'rgba(135, 135, 135, 0.08)'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#1E1E1E',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)'
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)'
        },
        elevation3: {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6)'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundColor: '#1E1E1E',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1A1A1A',
          borderRight: '1px solid rgba(255, 255, 255, 0.12)'
        }
      }
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(78, 205, 196, 0.16)',
            '&:hover': {
              backgroundColor: 'rgba(78, 205, 196, 0.24)'
            }
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#4ECDC4',
          '&:hover': {
            backgroundColor: 'rgba(78, 205, 196, 0.08)'
          }
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': {
            backgroundColor: 'rgba(78, 205, 196, 0.08)',
            cursor: 'pointer'
          }
        }
      }
    }
  }
});
